import React, {FunctionComponent, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {RouteNames} from "migration/pages";
import {KamiMenuItem} from "migration/entities/kamiMenuStruct";
import {IikoProductId, ItemId} from "migration/entities/baseAliases";
import {txt} from "migration/shared/lib/core/i18ngen";
import {Button} from "migration/shared/ui/Button";
import {Notify} from "migration/shared/lib/notification/notification";
import {Loading} from "migration/shared/ui/Loading";
import {useActions} from "migration/shared/lib/hooks/useActions";
import {useTypedSelector} from "migration/shared/lib/hooks/useTypedSelector";
import {CURRENCY} from "../../../../../utils/options/options";
import {Breadcrumbs} from "../../../../../components/breadcrumbs/breadcrumbs";
import {useGlobalContext, useUserContext} from "../../../../../hooks/globalContext";
import classes from "./IikoMenuBindings.module.scss";
import {bindableIikoProducts, iikoBindingSources, iikoProductSummary} from "../Iiko/store/binding";

export const IikoMenuBindings: FunctionComponent = () => {
    const navigate = useNavigate();
    const {i18n} = useGlobalContext();
    const {user, menu, currentMenuId, iikoActiveMenu} = useUserContext();
    const {currentLang} = useTypedSelector(state => state.lang);
    const {menuData, menuDataFor, isLoadingGetMenuData} = useTypedSelector(state => state.menuIiko);
    const iikoState = useTypedSelector(state => state.iiko);
    const {
        isLoadingGetIikoMenuSettings,
        isLoadingGetNomenclature,
        isLoadingGetIikoExternalMenu,
        isLoadingSaveIikoProductBindings,
    } = iikoState;
    // Every list and save below reads IIKO data only through iikoSources, so a
    // cached piece from another menu or a failed fetch never shows or is saved.
    const iikoSources = useMemo(() => iikoBindingSources(iikoState, currentMenuId), [iikoState, currentMenuId]);
    const iikoSettings = iikoSources.settings;
    const isLoadingIikoProducts = isLoadingGetIikoMenuSettings || isLoadingGetNomenclature || isLoadingGetIikoExternalMenu;

    const {
        fetchIikoNomenclature,
        fetchMenuData,
        saveIikoProductBindings,
        setIsLoadingGetMenuData,
        fetchIikoExternalMenu,
        fetchIikoMenuSettings
    } = useActions();

    const [selectedKamiProduct, setSelectedKamiProduct] = useState<ItemId>("");
    const [selectedIikoProduct, setSelectedIikoProduct] = useState<IikoProductId>("");
    const [selectedProductBinding, setSelectedProductBinding] = useState<ItemId>("");
    const [searchQueryKami, setSearchQueryKami] = useState<string>("");
    const [searchQueryBindings, setSearchQueryBindings] = useState<string>("");
    const [searchQueryIiko, setSearchQueryIiko] = useState<string>("");

    // The open menu's dishes; none while its fetch is pending or after it failed.
    const kamiMenuProducts: KamiMenuItem[] = useMemo(() => {
        const products: KamiMenuItem[] = [];
        if (menuDataFor !== currentMenuId) return products;

        menuData?.sections?.forEach((section) => {
            section?.categories?.forEach((category) => {
                category?.items?.forEach((item) => {
                    products.push(item);
                })
            })
        })

        return products;
    }, [menuData, menuDataFor, currentMenuId]);

    const kamiProducts: KamiMenuItem[] = useMemo(() => {
        const filteredProducts = kamiMenuProducts.filter(item => {
            return !Object.keys(iikoSettings?.productBindings || {}).includes(item.id);
        });

        if (searchQueryKami) {
            return filteredProducts.filter((item) => {
                return item?.name?.[menu?.i18n?.defaultLang]?.toLowerCase().includes(searchQueryKami.toLowerCase());
            });
        }

        return filteredProducts || [];
    }, [kamiMenuProducts, menu, searchQueryKami, iikoSettings]);

    const productBindings = useMemo(() => {
        const bindings = Object.entries(iikoSettings?.productBindings || {}).map(([kamiProductId, iikoProductId]) => ({
            kamiProduct: kamiMenuProducts.find((item) => item.id === kamiProductId) || {id: kamiProductId} as KamiMenuItem,
            iikoProductId,
            iikoProduct: iikoProductSummary(iikoSources, iikoProductId),
        }));

        if (searchQueryBindings) {
            const query = searchQueryBindings.toLowerCase();
            return bindings.filter((item) => {
                return item?.kamiProduct?.name?.[menu?.i18n?.defaultLang]?.toLowerCase().includes(query) ||
                    item.iikoProduct?.name?.toLowerCase().includes(query);
            });
        }

        return bindings;
    }, [iikoSources, iikoSettings, menu, searchQueryBindings, kamiMenuProducts]);

    const bindableProducts = useMemo(() => bindableIikoProducts(iikoSources), [iikoSources]);

    const iikoProducts = useMemo(() => {
        const query = searchQueryIiko.toLowerCase();
        return bindableProducts.filter(product => !query || product.name?.toLowerCase().includes(query));
    }, [bindableProducts, searchQueryIiko]);

    // A pick belongs to the menu it was made on. The header switches menus without
    // leaving this page, so the picks are dropped on a switch and checked on save.
    useEffect(() => {
        setSelectedKamiProduct("");
        setSelectedIikoProduct("");
        setSelectedProductBinding("");
    }, [currentMenuId]);

    const handleSelectKamiProduct = (kamiProductId: ItemId) => {
        if (selectedKamiProduct === kamiProductId) {
            setSelectedKamiProduct("");
            return;
        }
        setSelectedProductBinding("");
        setSelectedKamiProduct(kamiProductId);
    }

    const handleSelectProductBinding = (kamiProductId: ItemId) => {
        if (selectedProductBinding === kamiProductId) {
            setSelectedProductBinding("");
            return;
        }
        setSelectedIikoProduct("");
        setSelectedKamiProduct("");
        setSelectedProductBinding(kamiProductId);
    }

    const handleSelectIikoProduct = (iikoProductId: IikoProductId) => {
        if (selectedIikoProduct === iikoProductId) {
            setSelectedIikoProduct("");
            return;
        }
        setSelectedProductBinding("");
        setSelectedIikoProduct(iikoProductId);
    }

    const bindProduct = async () => {
        if (!iikoSettings) return;
        const isOffered = kamiMenuProducts.some(item => item.id === selectedKamiProduct)
            && !Object.keys(iikoSettings.productBindings || {}).includes(selectedKamiProduct)
            && bindableProducts.some(product => product.id === selectedIikoProduct);
        if (!isOffered) {
            setSelectedKamiProduct("");
            setSelectedIikoProduct("");
            Notify.Info({title: txt.please_select_kami_and_iiko_product[currentLang], message: ""})
            return;
        }

        setIsLoadingGetMenuData(true);
        await saveIikoProductBindings(
            {
                "menuId": currentMenuId,
                "organizationId": user?.org?.id || "",
                "productBindings": {
                    ...iikoSettings.productBindings,
                    [selectedKamiProduct]: selectedIikoProduct
                }
            },
            {navigate: navigate},
            true
        );
        setIsLoadingGetMenuData(false);
        setSelectedIikoProduct("");
        setSelectedKamiProduct("");
    }

    const unBindProduct = async (kamiProductId: ItemId) => {
        if (!iikoSettings) return;
        if (!Object.keys(iikoSettings.productBindings || {}).includes(kamiProductId)) {
            setSelectedProductBinding("");
            Notify.Info({title: txt.please_select_product_binding[currentLang], message: ""})
            return;
        }

        const bindings = {...iikoSettings.productBindings};
        delete bindings[kamiProductId];
        setIsLoadingGetMenuData(true);
        await saveIikoProductBindings(
            {
                "menuId": currentMenuId,
                "organizationId": user?.org?.id || "",
                "productBindings": bindings
            },
            {navigate: navigate},
            false
        );
        setIsLoadingGetMenuData(false);
        setSelectedProductBinding("");
    }

    useEffect(() => {
        if (iikoActiveMenu) {
            const controller = new AbortController();
            fetchIikoMenuSettings({menuId: currentMenuId}, controller, {navigate: navigate});
            return () => controller.abort();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iikoActiveMenu, currentMenuId]);

    // The product source is fetched once the settings are loaded for this menu, and
    // again only when the source changes, not after every binding save.
    useEffect(() => {
        if (!iikoSettings) return;
        const controller = new AbortController();
        if (iikoSettings.iikoExternalMenuId) {
            fetchIikoExternalMenu({
                iikoExternalMenuId: iikoSettings.iikoExternalMenuId,
                iikoPriceCategoryId: iikoSettings.iikoPriceCategoryId || null,
                organizationId: user?.org?.id || "",
                iikoOrganizationId: iikoSettings.iikoOrganizationId
            }, controller, {navigate: navigate});
        } else {
            fetchIikoNomenclature({
                organizationId: user?.org?.id || "",
                iikoOrganizationId: iikoSettings.iikoOrganizationId
            }, controller, {navigate: navigate});
        }
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iikoSettings?.iikoOrganizationId, iikoSettings?.iikoExternalMenuId, iikoSettings?.iikoPriceCategoryId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchMenuData({menuId: currentMenuId}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMenuId]);

    return (
        <div className={classes.main}>
            <div className={classes.container}>
                <Breadcrumbs
                    infos={[
                        {title: i18n.menus(), link: RouteNames.MENUS, index: "bracer-0"},
                        {
                            title: i18n.iiko_product_bindings(),
                            link: RouteNames.MENUS_IIKO_PRODUCT_BINDINGS,
                            index: "bracer-1"
                        }
                    ]}
                />

                <div className="app__header">
                    <div className="app__header-left">
                        <h1 className="app__header-title">{i18n.iiko_product_bindings()}</h1>
                    </div>

                    <div className={"app__header-right"} style={{display: "flex", gap: "20px"}}>
                        <Button
                            text={txt.unbind[currentLang]}
                            onClick={() => unBindProduct(selectedProductBinding)}
                            disabled={!selectedProductBinding}
                            isFilled={true}
                            isDelete={true}
                            loading={isLoadingSaveIikoProductBindings}
                        />
                        <Button
                            text={txt.bind[currentLang]}
                            onClick={bindProduct}
                            disabled={!selectedKamiProduct || !selectedIikoProduct}
                            isFilled={true}
                            loading={isLoadingSaveIikoProductBindings}
                        />
                    </div>
                </div>

                <div className={classes.card}>
                    <div className={classes.table}>
                        <div className={classes.table__column}>
                            <div className={classes.table__column__header}>
                                {`${txt.kami_products[currentLang]} - ${kamiProducts?.length || 0}`}
                            </div>

                            <input
                                value={searchQueryKami}
                                onChange={(e) => setSearchQueryKami(e.target.value)}
                                placeholder={txt.search_kami_products_by_name[currentLang]}
                                className={classes.table__column__searchbar__input}
                            />

                            <div
                                className={classes.table__column__products}
                                style={{
                                    justifyContent: isLoadingGetMenuData ? "center" : "",
                                    height: isLoadingGetMenuData ? "100%" : ""
                                }}
                            >
                                {isLoadingGetMenuData
                                    ?
                                    <Loading/>
                                    :
                                    !!kamiProducts?.length
                                        ?
                                        kamiProducts.map(item => {
                                            return (
                                                <div
                                                    key={item?.id}
                                                    onClick={() => handleSelectKamiProduct(item?.id)}
                                                    className={classes.product}
                                                    style={{
                                                        backgroundColor: selectedKamiProduct === item?.id ? "#0252CC" : "",
                                                        color: selectedKamiProduct === item?.id ? "#fff" : ""
                                                    }}
                                                >
                                                    <div className={classes.product__title}>
                                                        {item?.name?.[menu?.i18n?.defaultLang]}
                                                    </div>
                                                    <div className={classes.product__price}>
                                                        {`${item?.prices?.primary?.price / 100} ${CURRENCY[menu.i18n.currency]}`}
                                                    </div>
                                                    <div className={classes.product__id}>
                                                        {item?.id}
                                                    </div>
                                                </div>
                                            )
                                        })
                                        :
                                        <div className={classes.empty__list}>
                                            {txt.empty[currentLang]}
                                        </div>
                                }
                            </div>
                        </div>
                        <div className={classes.table__column}>
                            <div className={classes.table__column__header}>
                                {`${txt.bindings[currentLang]} - ${productBindings?.length || 0}`}
                            </div>

                            <input
                                value={searchQueryBindings}
                                onChange={(e) => setSearchQueryBindings(e.target.value)}
                                placeholder={txt.search_bindings_by_product_name[currentLang]}
                                className={classes.table__column__searchbar__input}
                            />

                            <div
                                className={classes.table__column__products}
                                style={{
                                    justifyContent: (isLoadingGetIikoMenuSettings || isLoadingGetNomenclature) ? "center" : "",
                                    height: (isLoadingGetIikoMenuSettings || isLoadingGetNomenclature) ? "100%" : ""
                                }}
                            >
                                {isLoadingGetIikoMenuSettings
                                    ?
                                    <Loading/>
                                    :
                                    !!productBindings.length
                                        ?
                                        productBindings.map((binding, index) => {
                                            return (
                                                <div
                                                    key={`${binding?.kamiProduct?.id} - ${binding.iikoProductId} - ${index}`}
                                                    onClick={() => handleSelectProductBinding(binding?.kamiProduct?.id)}
                                                    className={classes.product__card}
                                                    style={{
                                                        marginBottom: "10px",
                                                        backgroundColor: selectedProductBinding === binding?.kamiProduct?.id ? "#0252CC" : "",
                                                        color: selectedProductBinding === binding?.kamiProduct?.id ? "#fff" : ""
                                                    }}
                                                >
                                                    <div
                                                        className={classes.product}
                                                        style={{borderBottom: "1px dashed #E5E5E5"}}
                                                    >
                                                        <div className={classes.product__title}>
                                                            {"KAMI"}
                                                        </div>
                                                        <div className={classes.product__title}>
                                                            {binding?.kamiProduct?.name?.[menu?.i18n?.defaultLang] || txt.product_not_found[currentLang]}
                                                        </div>
                                                        {!!binding?.kamiProduct?.prices?.primary?.price && (
                                                            <div className={classes.product__price}>
                                                                {`${binding?.kamiProduct?.prices?.primary?.price / 100} ${CURRENCY[menu.i18n.currency]}`}
                                                            </div>
                                                        )}
                                                        <div className={classes.product__id} style={{opacity: "1"}}>
                                                            {binding?.kamiProduct?.id}
                                                        </div>
                                                    </div>

                                                    {/*<React.Fragment>*/}
                                                    {/*    <div className={classes.product__card__title}>{"kami"}</div>*/}
                                                    {/*    <VerticalAlignMiddleOutlined*/}
                                                    {/*        className={classes.product__card__icon}/>*/}
                                                    {/*    <div className={classes.product__card__title}>{"iiko"}</div>*/}
                                                    {/*</React.Fragment>*/}

                                                    <div
                                                        className={classes.product}
                                                        style={{color: selectedProductBinding === binding?.kamiProduct?.id ? "#fff" : "#fc393d"}}
                                                    >
                                                        <div className={classes.product__title}>
                                                            {"IIKO"}
                                                        </div>
                                                        <div className={classes.product__title}>
                                                            {binding.iikoProduct?.name || txt.product_not_found[currentLang]}
                                                        </div>
                                                        {!!binding.iikoProduct?.price && (
                                                            <div className={classes.product__price}>
                                                                {`${binding.iikoProduct.price} ${CURRENCY[menu.i18n.currency]}`}
                                                            </div>
                                                        )}
                                                        <div className={classes.product__id} style={{opacity: "1"}}>
                                                            {binding.iikoProductId}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })
                                        :
                                        <div className={classes.empty__list}>
                                            {txt.empty[currentLang]}
                                        </div>
                                }
                            </div>
                        </div>
                        <div className={classes.table__column}>
                            <div className={classes.table__column__header}>
                                {`${txt.iiko_products[currentLang]} - ${iikoProducts.length}`}
                            </div>

                            <input
                                value={searchQueryIiko}
                                onChange={(e) => setSearchQueryIiko(e.target.value)}
                                placeholder={txt.search_iiko_products_by_name[currentLang]}
                                className={classes.table__column__searchbar__input}
                            />

                            <div
                                className={classes.table__column__products}
                                style={{
                                    justifyContent: isLoadingIikoProducts ? "center" : "",
                                    height: isLoadingIikoProducts ? "100%" : ""
                                }}
                            >
                                {isLoadingIikoProducts
                                    ?
                                    <Loading/>
                                    :
                                    !!iikoProducts.length
                                        ?
                                        iikoProducts.map((iikoProduct) => {
                                            return (
                                                <div
                                                    key={iikoProduct.id}
                                                    onClick={() => handleSelectIikoProduct(iikoProduct.id)}
                                                    className={classes.product}
                                                    style={{
                                                        backgroundColor: selectedIikoProduct === iikoProduct.id ? "#0252CC" : "",
                                                        color: selectedIikoProduct === iikoProduct.id ? "#fff" : ""
                                                    }}
                                                >
                                                    <div className={classes.product__title}>
                                                        {iikoProduct.name}
                                                    </div>
                                                    <div className={classes.product__price}>
                                                        {`${iikoProduct.prices} ${CURRENCY[menu.i18n.currency]}`}
                                                    </div>
                                                    <div className={classes.product__id}>
                                                        {iikoProduct.id}
                                                    </div>
                                                </div>
                                            )
                                        })
                                        :
                                        <div className={classes.empty__list}>
                                            {txt.empty[currentLang]}
                                        </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
